import { useId, useState } from 'react'
import { ArrowDown, ArrowUp, Camera, Check, Crosshair, Loader2, X } from 'lucide-react'
import type { SurveyQuestionDto, SurveyQuestionOptionDto } from '@/shared/api/types'
import { Note } from '@/shared/ui/states'
import { cn } from '@/lib/utils'
import { captureLocation, accuracyNote } from '@/shared/geo/geolocation'
import {
  isOutsideBase, optionLabel, orderedOptions, questionHelp, questionText, subQuestions,
  type DraftAnswer,
} from './captureSession'
import { addPhotoClip, useRecorder } from './AudioRecorder'
import { questionTypeLabel } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * One control per question type, sized for a thumb.
 *
 * ## Why every target is at least 52px and full width
 *
 * This screen is used standing up, outdoors, holding a phone in one hand and
 * often a clipboard in the other, with a respondent waiting. A 36px row that is
 * comfortable with a mouse is a mis-tap with a thumb, and a mis-tap here is a
 * wrong answer that nothing downstream can detect — it looks exactly like a
 * respondent who said that. The shell already promotes controls to 44px under
 * `@media (pointer: coarse)`; the answer options go further because they are the
 * thing being aimed at.
 *
 * ## `DONT_KNOW` and `REFUSED` are ordinary buttons and look different
 *
 * They are offered, never hidden behind a long-press or a menu: an interviewer
 * who cannot record "don't know" records a guess instead, and a guess is
 * indistinguishable from data. They are also visually distinct from the
 * substantive options and always last, because they sit OUTSIDE the base — a
 * percentage computed on `SUBSTANTIVE` excludes them, and a respondent scanning
 * the list should not have to read past them to find a real answer.
 *
 * ## RANKING is a list with arrows
 *
 * Not a comma-separated text box under a label reading "drag to rank", which is
 * what PollSphere shipped. That control cannot be operated by the person it is
 * labelled for, cannot be validated, and produces free text where the analysis
 * expects an ordered set of option codes.
 */

export interface QuestionInputProps {
  question: SurveyQuestionDto
  /** The whole instrument, so a MATRIX can find its rows. */
  all: SurveyQuestionDto[]
  language: string
  seed: number
  randomiseGlobally: boolean
  answers: Record<string, DraftAnswer>
  onChange: (questionId: string, patch: DraftAnswer | null) => void
  /** Question id → the rule that hid it, from the skip evaluator. A MATRIX row can be hidden. */
  hiddenBy?: Record<string, string>
}

export function QuestionInput(props: QuestionInputProps) {
  const { question, answers } = props
  const value = answers[question.id]

  switch (question.typeCode) {
    case 'SINGLE_CHOICE':
    case 'CONSENT':
      return <SingleChoice {...props} value={value} />
    case 'MULTI_CHOICE':
      return <MultiChoice {...props} value={value} />
    case 'YES_NO':
      return <YesNo {...props} value={value} />
    case 'LIKERT':
      // A Likert item normally carries its five labelled points as options. When
      // it does, the labels are the answer and a bare number would lose them.
      return question.options.length > 0
        ? <SingleChoice {...props} value={value} />
        : <Scale {...props} value={value} />
    case 'RATING_SCALE':
    case 'NPS':
      return <Scale {...props} value={value} />
    case 'RANKING':
      return <Ranking {...props} value={value} />
    case 'NUMBER':
      return <NumberAnswer {...props} value={value} />
    case 'OPEN_TEXT':
      return <OpenText {...props} value={value} />
    case 'DATE':
      return <DateAnswer {...props} value={value} />
    case 'GEOLOCATION':
      return <GeoAnswer {...props} value={value} />
    case 'MEDIA_CAPTURE':
      return <MediaAnswer {...props} value={value} />
    case 'MATRIX':
      return <Matrix {...props} />
    default:
      // A type this build has never heard of is the forward-compatibility
      // mechanism working. It gets a text box rather than a blank screen, and it
      // says what it is so the answer can be read back and recoded.
      return (
        <div className="space-y-2">
          <Note tone="info">
            This build does not have a purpose-built control for a
            {' '}<strong>{questionTypeLabel(question.typeCode)}</strong> question. Record the
            answer as text — it is stored against the question either way and can be recoded.
          </Note>
          <OpenText {...props} value={value} />
        </div>
      )
  }
}

type Inner = QuestionInputProps & { value: DraftAnswer | undefined }

// ── choice ──────────────────────────────────────────────────────────────────

function SingleChoice({
  question, language, seed, randomiseGlobally, value, onChange,
}: Inner) {
  const options = orderedOptions(question, seed, randomiseGlobally)
  const chosen = value?.optionCodes?.[0] ?? null
  const chosenOption = options.find((o) => o.code === chosen) ?? null

  return (
    <div className="space-y-2">
      <div className="grid gap-2">
        {options.map((o) => (
          <OptionButton
            key={o.id}
            option={o}
            language={language}
            selected={chosen === o.code}
            onClick={() => onChange(
              question.id,
              // Tapping the chosen option again clears it. There is no other way
              // back to "not answered" on a radio group, and "I already recorded
              // that by mistake" is a thing that happens standing up.
              chosen === o.code ? null : { optionCodes: [o.code] },
            )}
          />
        ))}
      </div>
      {chosenOption?.kindCode === 'OTHER_SPECIFY' && (
        <OtherSpecify
          value={value?.otherText ?? ''}
          onChange={(otherText) => onChange(question.id, { optionCodes: [chosenOption.code], otherText })}
        />
      )}
    </div>
  )
}

function MultiChoice({
  question, language, seed, randomiseGlobally, value, onChange,
}: Inner) {
  const options = orderedOptions(question, seed, randomiseGlobally)
  const chosen = value?.optionCodes ?? []
  const min = question.minSelections
  const max = question.maxSelections
  const other = options.find((o) => o.kindCode === 'OTHER_SPECIFY' && chosen.includes(o.code))

  function toggle(o: SurveyQuestionOptionDto) {
    const on = chosen.includes(o.code)
    let next: string[]
    if (on) {
      next = chosen.filter((c) => c !== o.code)
    } else if (o.exclusive || isOutsideBase(o)) {
      // An exclusive option means "and nothing else" — "None of the above" and
      // "Refused" are only true alone. Selecting one clears the rest rather than
      // silently coexisting with them, which would submit a contradiction.
      next = [o.code]
    } else {
      const kept = chosen.filter((c) => {
        const existing = options.find((x) => x.code === c)
        return existing ? !(existing.exclusive || isOutsideBase(existing)) : true
      })
      next = [...kept, o.code]
    }
    onChange(question.id, next.length === 0 ? null : { optionCodes: next, otherText: value?.otherText })
  }

  // The cap is enforced by disabling the UNCHOSEN options once it is reached —
  // and the sentence beside them says why, because a control that goes grey
  // without explanation reads as broken.
  const atMax = max !== null && max !== undefined && chosen.length >= max
  const belowMin = min !== null && min !== undefined && chosen.length > 0 && chosen.length < min

  return (
    <div className="space-y-2">
      {(min || max) && (
        <p className="text-erp-xs text-muted-foreground">
          {min && max ? `Choose between ${min} and ${max}.`
            : min ? `Choose at least ${min}.` : `Choose up to ${max}.`}
        </p>
      )}
      <div className="grid gap-2">
        {options.map((o) => {
          const on = chosen.includes(o.code)
          return (
            <OptionButton
              key={o.id}
              option={o}
              language={language}
              selected={on}
              multi
              disabled={!on && atMax && !o.exclusive && !isOutsideBase(o)}
              onClick={() => toggle(o)}
            />
          )
        })}
      </div>
      {atMax && (
        <p className="text-erp-xs text-muted-foreground">
          That is the maximum of {max}. Unselect one to choose a different answer.
        </p>
      )}
      {belowMin && (
        <p className="text-erp-xs text-amber-700">
          {chosen.length} of at least {min} chosen.
        </p>
      )}
      {other && (
        <OtherSpecify
          value={value?.otherText ?? ''}
          onChange={(otherText) => onChange(question.id, { optionCodes: chosen, otherText })}
        />
      )}
    </div>
  )
}

function OptionButton({
  option, language, selected, multi, disabled, onClick,
}: {
  option: SurveyQuestionOptionDto
  language: string
  selected: boolean
  multi?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  const outside = isOutsideBase(option)
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-3 py-3 text-left text-erp-md',
        'min-h-[52px] transition-colors disabled:opacity-40',
        outside
          // Outside the base, and it looks it: dashed, muted, italic. The
          // difference has to be visible at arm's length in sunlight.
          ? 'border-dashed border-border bg-muted/30 text-muted-foreground italic'
          : 'border-border bg-background hover:bg-muted/40',
        selected && !outside && 'border-chocolate bg-chocolate-soft/40 text-foreground',
        selected && outside && 'border-foreground/40 bg-muted text-foreground not-italic',
      )}
    >
      <span className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center border',
        multi ? 'rounded' : 'rounded-full',
        selected ? 'border-chocolate bg-chocolate text-white' : 'border-input bg-background',
      )}>
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0">{optionLabel(option, language)}</span>
    </button>
  )
}

function OtherSpecify({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const id = useId()
  return (
    <div className="space-y-1.5 pl-3 border-l-2 border-chocolate/40">
      <label htmlFor={id} className="text-erp-xs font-medium text-muted-foreground block">
        In their own words
      </label>
      <input
        id={id}
        className="erp-input w-full"
        value={value}
        placeholder="What did they say?"
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-erp-xs text-muted-foreground">
        Recorded as written and coded later. The option code alone would lose the answer.
      </p>
    </div>
  )
}

// ── yes / no ────────────────────────────────────────────────────────────────

function YesNo({ question, language, seed, randomiseGlobally, value, onChange }: Inner) {
  // An instrument may carry explicit YES/NO options — with their own codes and
  // translations — and when it does those are what the branches compare against.
  if (question.options.length > 0) {
    return (
      <SingleChoice
        question={question} all={[]} language={language} seed={seed}
        randomiseGlobally={randomiseGlobally} answers={{}} value={value} onChange={onChange}
      />
    )
  }
  const chosen = value?.bool
  return (
    <div className="grid grid-cols-2 gap-2">
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          aria-pressed={chosen === v}
          onClick={() => onChange(question.id, chosen === v ? null : { bool: v })}
          className={cn(
            'min-h-[64px] rounded-md border text-erp-lg font-medium transition-colors',
            chosen === v
              ? 'border-chocolate bg-chocolate-soft/40'
              : 'border-border bg-background hover:bg-muted/40',
          )}
        >
          {v ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  )
}

// ── scales ──────────────────────────────────────────────────────────────────

function Scale({ question, value, onChange }: Inner) {
  // NPS is definitionally 0–10, so an instrument that declares nothing gets the
  // definition rather than a made-up range. Anything else without bounds is a
  // gap in the instrument and says so — inventing 1–5 would silently rescale
  // every answer the analysis reads back.
  const min = question.scaleMin ?? (question.typeCode === 'NPS' ? 0 : null)
  const max = question.scaleMax ?? (question.typeCode === 'NPS' ? 10 : null)
  const chosen = value?.number

  if (min === null || max === null || max <= min) {
    return (
      <div className="space-y-2">
        <Note tone="bad">
          This {questionTypeLabel(question.typeCode).toLowerCase()} question has no scale
          declared on the instrument, so there are no points to offer. Record the answer as a
          number and tell whoever built the poll — the bounds belong in the Builder, not in a
          guess made here.
        </Note>
        <NumberAnswer
          question={question} all={[]} language="" seed={0} randomiseGlobally={false}
          answers={{}} value={value} onChange={onChange}
        />
      </div>
    )
  }

  const points: number[] = []
  for (let i = min; i <= max; i += 1) points.push(i)

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {points.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={chosen === String(p)}
            onClick={() => onChange(question.id, chosen === String(p) ? null : { number: String(p) })}
            className={cn(
              'min-h-[52px] min-w-[52px] flex-1 rounded-md border text-erp-lg tabular-nums font-medium',
              'transition-colors',
              chosen === String(p)
                ? 'border-chocolate bg-chocolate-soft/40'
                : 'border-border bg-background hover:bg-muted/40',
            )}
          >
            {p}
          </button>
        ))}
      </div>
      {question.typeCode === 'NPS' && (
        <p className="text-erp-xs text-muted-foreground">
          0 is “not at all likely”, 10 is “extremely likely”. Read both ends out loud — the
          scale means nothing to a respondent who has only heard the number.
        </p>
      )}
      {/*
        DK and REFUSED are options on the instrument even for a scale, and they
        stay reachable here rather than forcing a number nobody said.
      */}
      <OutsideBaseRow question={question} value={value} onChange={onChange} />
    </div>
  )
}

/** The DK / REFUSED escape hatch, for the types whose main control is not a list. */
function OutsideBaseRow({
  question, value, onChange,
}: { question: SurveyQuestionDto; value: DraftAnswer | undefined; onChange: QuestionInputProps['onChange'] }) {
  const outside = question.options.filter(isOutsideBase)
  if (outside.length === 0) return null
  const chosen = value?.optionCodes?.[0] ?? null
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {outside.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={chosen === o.code}
          onClick={() => onChange(question.id, chosen === o.code ? null : { optionCodes: [o.code] })}
          className={cn(
            'min-h-[48px] rounded-md border border-dashed px-4 text-erp-sm italic',
            chosen === o.code
              ? 'border-foreground/40 bg-muted text-foreground not-italic'
              : 'border-border bg-muted/30 text-muted-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ── ranking ─────────────────────────────────────────────────────────────────

/**
 * A real ordered list.
 *
 * Options start unranked and are promoted into the ranking by tapping; once in,
 * they move with two arrows. Two arrows rather than drag-and-drop because
 * dragging inside a scrolling page on a touch screen fights the scroll, and the
 * failure mode is a respondent's ranking silently reordered by a stray swipe.
 *
 * The submitted `optionCodes` are the ranking, in order. That is what the
 * contract's `List<String>` means and it is the only representation the analysis
 * can read back — a comma-separated string in `text` is not.
 */
function Ranking({ question, language, seed, randomiseGlobally, value, onChange }: Inner) {
  const options = orderedOptions(question, seed, randomiseGlobally)
  const ranked = value?.optionCodes ?? []
  const unranked = options.filter((o) => !ranked.includes(o.code) && !isOutsideBase(o))
  const byCode = new Map(options.map((o) => [o.code, o]))

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= ranked.length) return
    const next = [...ranked]
    const a = next[index]!
    const b = next[target]!
    next[index] = b
    next[target] = a
    onChange(question.id, { optionCodes: next })
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {ranked.length === 0 && (
          <p className="text-erp-xs text-muted-foreground">
            Nothing ranked yet. Tap the answers below in the order the respondent gives them.
          </p>
        )}
        {ranked.map((code, i) => {
          const o = byCode.get(code)
          return (
            <div key={code}
              className="flex items-center gap-2 rounded-md border border-chocolate/40 bg-chocolate-soft/30 px-3 py-2 min-h-[52px]">
              <span className="w-6 shrink-0 text-erp-lg font-semibold tabular-nums text-chocolate">
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 text-erp-md">
                {o ? optionLabel(o, language) : code}
              </span>
              <button type="button" aria-label={`Move ${o?.label ?? code} up`}
                disabled={i === 0} onClick={() => move(i, -1)}
                className="axis-icon-btn h-11 w-11 inline-flex items-center justify-center rounded-md border border-border bg-background disabled:opacity-30">
                <ArrowUp className="h-4 w-4" />
              </button>
              <button type="button" aria-label={`Move ${o?.label ?? code} down`}
                disabled={i === ranked.length - 1} onClick={() => move(i, 1)}
                className="axis-icon-btn h-11 w-11 inline-flex items-center justify-center rounded-md border border-border bg-background disabled:opacity-30">
                <ArrowDown className="h-4 w-4" />
              </button>
              <button type="button" aria-label={`Remove ${o?.label ?? code} from the ranking`}
                onClick={() => onChange(
                  question.id,
                  ranked.length === 1 ? null : { optionCodes: ranked.filter((c) => c !== code) },
                )}
                className="axis-icon-btn h-11 w-11 inline-flex items-center justify-center rounded-md border border-border bg-background">
                <X className="h-4 w-4" />
              </button>
            </div>
          )
        })}
      </div>

      {unranked.length > 0 && (
        <div className="space-y-2">
          <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">
            Not yet ranked
          </p>
          {unranked.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(question.id, { optionCodes: [...ranked, o.code] })}
              className="flex w-full items-center gap-3 rounded-md border border-border bg-background px-3 py-3 text-left text-erp-md min-h-[52px] hover:bg-muted/40"
            >
              <span className="text-erp-xs text-muted-foreground w-6 shrink-0">
                {ranked.length + 1}
              </span>
              {optionLabel(o, language)}
            </button>
          ))}
        </div>
      )}

      <OutsideBaseRow question={question} value={value} onChange={onChange} />
    </div>
  )
}

// ── number, text, date ──────────────────────────────────────────────────────

function NumberAnswer({ question, language, value, onChange }: Inner) {
  const min = question.numericMin === null || question.numericMin === undefined
    ? null : Number(question.numericMin)
  const max = question.numericMax === null || question.numericMax === undefined
    ? null : Number(question.numericMax)
  const raw = value?.number ?? ''
  const n = raw === '' ? null : Number(raw)
  // Out of range is SHOWN, not blocked. A respondent who says 130 has said 130,
  // and clamping it silently would record a number nobody gave; the server has
  // the same bounds and refuses on submit if it must.
  const low = n !== null && min !== null && n < min
  const high = n !== null && max !== null && n > max

  return (
    <div className="space-y-1.5">
      <input
        // The question is a `<p>` in the step above and every other control in
        // this feature gets its name from `Field`. Without this the box is the
        // one thing on the screen a screen reader announces as "edit text,
        // blank" — on the single screen in the product used one-handed, outdoors,
        // with a respondent waiting.
        aria-label={questionText(question, language)}
        // `inputMode` and not `type="number"`: the numeric keypad appears either
        // way, and a number input silently discards a value the browser thinks is
        // malformed — including mid-typing — which loses keystrokes on a phone.
        inputMode="decimal"
        className="erp-input w-full text-erp-2xl h-14 tabular-nums"
        value={raw}
        onChange={(e) => {
          const next = e.target.value
          onChange(question.id, next.trim() === '' ? null : { number: next })
        }}
      />
      {(min !== null || max !== null) && (
        <p className="text-erp-xs text-muted-foreground">
          {min !== null && max !== null ? `Expected between ${min} and ${max}.`
            : min !== null ? `Expected at least ${min}.` : `Expected at most ${max}.`}
        </p>
      )}
      {(low || high) && (
        <p className="text-erp-xs text-amber-700">
          {raw} is outside the range this question expects. It is kept as given — check it was
          heard right before moving on.
        </p>
      )}
      <OutsideBaseRow question={question} value={value} onChange={onChange} />
    </div>
  )
}

function OpenText({ question, language, value, onChange }: Inner) {
  return (
    <div className="space-y-1.5">
      <textarea
        // Named by the question it answers. A verbatim box with no accessible
        // name is announced as "edit text, blank", which on this screen means an
        // answer typed into the wrong question.
        aria-label={questionText(question, language)}
        rows={5}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-erp-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        value={value?.text ?? ''}
        placeholder="Their answer, in their words"
        onChange={(e) => {
          const next = e.target.value
          onChange(question.id, next.trim() === '' ? null : { text: next })
        }}
      />
      <p className="text-erp-xs text-muted-foreground">
        Write what they said, not a summary. Verbatims are coded afterwards and a paraphrase
        codes as the interviewer's opinion.
      </p>
      <OutsideBaseRow question={question} value={value} onChange={onChange} />
    </div>
  )
}

function DateAnswer({ question, language, value, onChange }: Inner) {
  return (
    <div className="space-y-1.5">
      <DatePicker
        // Without a label the control is announced only as "select a date"; the
        // question text is the only thing that says WHICH date is being asked for.
        aria-label={questionText(question, language)}
        className="h-14 text-erp-md"
        value={value?.text ?? ''}
        onChange={(next) => onChange(question.id, next === '' ? null : { text: next })}
      />
      <OutsideBaseRow question={question} value={value} onChange={onChange} />
    </div>
  )
}

// ── geolocation as an ANSWER ────────────────────────────────────────────────

/**
 * A `GEOLOCATION` question is not the interview's own fix.
 *
 * The submission already carries where the interview happened; this is a place
 * the respondent is being asked ABOUT — where they will vote, where the clinic
 * is — so it is captured separately and stored as the answer's text. The wire has
 * one pair of coordinates per response and it belongs to the response, so the
 * pair is written into `text` in a fixed `lat,lon` form the export can parse
 * rather than being smuggled into the response's own geo fields.
 */
function GeoAnswer({ question, value, onChange }: Inner) {
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [accuracy, setAccuracy] = useState<number | null>(null)

  async function take() {
    setBusy(true)
    setProblem(null)
    const outcome = await captureLocation()
    if (outcome.ok) {
      setAccuracy(outcome.location.accuracyM)
      onChange(question.id, {
        text: `${outcome.location.latitude.toFixed(6)},${outcome.location.longitude.toFixed(6)}`,
      })
    } else {
      setProblem(outcome.reason)
    }
    setBusy(false)
  }

  return (
    <div className="space-y-2">
      <button type="button" onClick={take} disabled={busy} className="erp-btn h-12 w-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
        {value?.text ? 'Take the fix again' : 'Take a location fix'}
      </button>
      {value?.text && (
        <p className="text-erp-sm tabular-nums">
          {value.text}
          {accuracy !== null && (
            <span className="block text-erp-xs text-muted-foreground">
              {accuracyNote(accuracy)}
            </span>
          )}
        </p>
      )}
      {problem && <Note tone="bad">{problem}</Note>}
      <OutsideBaseRow question={question} value={value} onChange={onChange} />
    </div>
  )
}

// ── media capture ───────────────────────────────────────────────────────────

/**
 * A photo for this question.
 *
 * Held with the interview's other media and uploaded after submission, because
 * `reserveMedia` needs a `responseId` that does not exist yet. The answer itself
 * records WHEN the photo was taken: the image is linked to the question by the
 * media row's `questionId`, and a text field carrying a filename would be a fact
 * about this device rather than about the interview.
 */
function MediaAnswer({ question, value, onChange }: Inner) {
  const id = useId()
  // Read through the recorder store rather than a one-off lookup: the clip's
  // upload state changes after submission and this row has to follow it.
  const rec = useRecorder()
  const existing = rec.clips.find((c) => c.kindCode === 'PHOTO' && c.questionId === question.id)

  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className="erp-btn h-12 w-full cursor-pointer"
      >
        <Camera className="h-4 w-4" />
        {existing ? 'Take another photo' : 'Take a photo'}
      </label>
      <input
        id={id}
        type="file"
        accept="image/*"
        // `capture` opens the camera directly instead of the gallery. It is a
        // hint, not a guarantee — a desktop browser ignores it and offers a file
        // picker, which is the right fallback rather than an error.
        capture="environment"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return
          const clip = addPhotoClip(question.id, file)
          onChange(question.id, { text: clip.capturedAt })
        }}
      />
      {existing && (
        <p className="text-erp-xs text-muted-foreground">
          Photo held ({(existing.sizeBytes / (1024 * 1024)).toFixed(1)} MB). It uploads once the
          interview is submitted — an image needs an interview to belong to.
        </p>
      )}
      {value?.text && !existing && (
        <Note tone="bad">
          The photo taken for this question is no longer held on this device. Take it again, or
          the answer will arrive with nothing attached.
        </Note>
      )}
      <OutsideBaseRow question={question} value={value} onChange={onChange} />
    </div>
  )
}

// ── matrix ──────────────────────────────────────────────────────────────────

/**
 * A battery of rows sharing one scale.
 *
 * IN SCOPE, rendered as a group. The alternative — walking each sub-question as
 * its own screen — makes the interviewer read "on the same scale from 1 to 5…"
 * once per row, which is both slower and a different question: a matrix is
 * administered as a block precisely so the respondent holds one scale in mind.
 * The straightlining and non-differentiation integrity rules are defined over
 * exactly this block, and they only mean anything if it was asked as one.
 *
 * A row uses its own options when it declares them and the parent's when it does
 * not, which is how a shared scale is normally authored.
 */
function Matrix(props: QuestionInputProps) {
  const { question, all, language, answers, onChange, hiddenBy } = props
  // A row a branch has hidden is not asked. It is not blank either — the server
  // records it as skipped — so it must not appear in the block at all.
  const rows = subQuestions(all, question).filter((r) => !hiddenBy?.[r.id])

  if (rows.length === 0) {
    return (
      <Note tone="info">
        This matrix has no rows on the published instrument, so there is nothing to ask. Move
        on and tell whoever built the poll.
      </Note>
    )
  }

  return (
    <div className="space-y-4">
      {rows.map((row) => (
        <div key={row.id} className="rounded-md border border-border p-3 space-y-2">
          <p className="text-erp-md font-medium">{questionText(row, language)}</p>
          {questionHelp(row, language) && (
            <p className="text-erp-xs text-muted-foreground">{questionHelp(row, language)}</p>
          )}
          <QuestionInput
            {...props}
            question={row.options.length > 0 ? row : { ...row, options: question.options }}
            answers={answers}
            onChange={onChange}
          />
        </div>
      ))}
    </div>
  )
}
